import { IoSearch } from 'react-icons/io5';
import { IoMdClose } from 'react-icons/io';
import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useTour } from '@reactour/tour';

const MAX_CHARACTERS = 50;

interface SearchBarProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
}

export const SearchBar = ({ searchValue, setSearchValue }: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(searchValue);
  const debouncedValue = useDebounce(inputValue, 250);

  useEffect(() => {
    setSearchValue(debouncedValue);
  }, [debouncedValue, setSearchValue]);

  const handleClear = () => {
    setInputValue('');
    setSearchValue('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setInputValue(value);
    }
  };

  const { currentStep, isOpen } = useTour();

  useEffect(() => {
    if ((currentStep === 3 || currentStep === 0) && isOpen) {
      setInputValue('');
    }
  }, [currentStep, isOpen]);

  useEffect(() => {
    const onTourClose = () => {
      setInputValue('');
    };
    window.addEventListener('reactour-close', onTourClose);
    return () => window.removeEventListener('reactour-close', onTourClose);
  }, []);

  return (
    <div className="relative flex-1">
      <div
        className={`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 transition-opacity duration-300 ${
          isFocused ? 'opacity-100' : 'opacity-50'
        }`}
      >
        <IoSearch className="h-5 w-5 text-neutralText dark:text-neutralText-dark" />
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        maxLength={MAX_CHARACTERS}
        className={`text-md h-9 w-11/12 select-none rounded-md border border-neutralText border-opacity-20 bg-[transparent] pl-9 pr-20 font-normal outline-none transition-all duration-300 ease-in-out dark:border-neutralText-dark dark:border-opacity-20 sm:w-[300px] ${
          isFocused
            ? '!dark:border-primary border-2 border-solid !border-primary sm:w-[350px]'
            : '!shadow-none hover:border-opacity-30 dark:hover:border-opacity-30'
        }`}
        placeholder="Rechercher un fichier..."
        style={{
          boxShadow: 'none',
        }}
      />
      <div
        className={`absolute top-1/2 -translate-y-1/2 text-xs text-neutralText opacity-50 transition-all duration-300 dark:text-neutralText-dark ${inputValue ? 'right-9' : 'right-3'}`}
      >
        {inputValue.length}/{MAX_CHARACTERS}
      </div>
      {inputValue && (
        <button
          onClick={handleClear}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutralText opacity-50 transition-opacity duration-300 hover:opacity-100 dark:text-neutralText-dark"
        >
          <IoMdClose className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};
